<i18n locale="en">
{
  "empty": "Scrolling Banner"
}
</i18n>

<template>
  <PreviewSheet
    class="scrolling-preview w-full"
    radius="0"
    :background="hasBackgroundColor ? 'transparent' : 'primary'"
    :style="containerStyle"
  >
    <div class="scrolling-preview__track flex-row gap-16">
      <PreviewTypography
        v-for="(item, index) in content.params.items"
        :key="index"
        :text="item.text"
        variant="titleRegular"
        :color="textColor"
        class="scrolling-preview__item"
      />
      <PreviewTypography
        v-if="content.params.items.length === 0"
        :text="t('empty')"
        variant="titleRegular"
        color="secondary"
      />
    </div>
  </PreviewSheet>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import PreviewSheet from '@/lib/builder/public/preview/PreviewSheet.vue'
import PreviewTypography from '@/lib/builder/public/preview/PreviewTypography.vue'
import { useExtendedMergedTheme } from '@/lib/theme/useExtendedMergedTheme'
import { tokenToCss, pickTextColor } from '@/utils/backgroundColors'

import type { ScrollingSection } from '../../../WebsiteBuilder.types'

const props = defineProps<{
  content: ScrollingSection
}>()

const { t } = useI18n()

const theme = useExtendedMergedTheme()

const backgroundToken = computed(() =>
  props.content.params.background?.type === 'color'
    ? props.content.params.background.params.token
    : null
)

const hasBackgroundColor = computed(() => !!backgroundToken.value)

const containerStyle = computed(() => {
  if (!backgroundToken.value) return undefined
  const css = tokenToCss(backgroundToken.value, theme.value)
  return css ? { backgroundColor: css } : undefined
})

const textColor = computed(() => pickTextColor(backgroundToken.value))
</script>

<style lang="scss" scoped>
.scrolling-preview {
  padding: 32px 120px;
  overflow: hidden;

  &__track {
    white-space: nowrap;
    overflow: hidden;
  }

  &__item {
    flex-shrink: 0;
  }
}
</style>
